import React from 'react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { api, useQuery } from '../../lib/convex';
import type { Id } from '../../lib/convex';

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams({ from: '/quiz/$sessionId/results' });
  const { participantId } = useSearch({ from: '/quiz/$sessionId/results' });
  
  const participant = useQuery(api.participants.get, {
    id: participantId as Id<"participants">
  });

  const participantAnswers = useQuery(api.answers.getParticipantAnswers, {
    sessionId: sessionId as Id<"sessions">,
    participantId: participantId as Id<"participants">
  });

  const topScores = useQuery(api.participants.getTopScores, {
    sessionId: sessionId as Id<"sessions">
  });

  const session = useQuery(api.sessions.get, {
    id: sessionId as Id<"sessions">
  });

  const quiz = useQuery(api.quizzes.get, session?.quizId ? { id: session.quizId } : "skip");

  if (!participant || !participantAnswers || !topScores || !quiz || !session) {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  const finalScore = participant.score;
  const correctAnswers = participantAnswers.filter(a => a.isCorrect).length;
  const totalQuestions = quiz.questions.length;
  const rank = topScores.findIndex(p => p._id === participantId) + 1;
  const totalParticipants = topScores.length;
  
  return (
    <div className="container max-w-md mx-auto px-4 py-8">
      <Card className="text-center">
        <CardHeader>
          <CardTitle className="text-3xl font-heading">Quiz Completed!</CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="py-6">
            <div className="text-6xl font-heading font-bold text-primary mb-2">{finalScore}</div>
            <div className="text-muted-foreground font-semibold">points</div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-secondary/50 p-5 rounded-xl">
              <div className="text-3xl font-heading font-bold">{correctAnswers}/{totalQuestions}</div>
              <div className="text-sm text-muted-foreground font-medium">Correct Answers</div>
            </div>
            
            <div className="bg-secondary/50 p-5 rounded-xl">
              <div className="text-3xl font-heading font-bold">#{rank}</div>
              <div className="text-sm text-muted-foreground font-medium">Rank out of {totalParticipants}</div>
            </div>
          </div>
          
          {rank === 1 && (
            <div className="py-4 mb-6">
              <div className="text-5xl mb-2">🏆</div>
              <div className="font-heading font-bold text-xl">You're the Winner!</div>
            </div>
          )}
          
          <div className="space-y-3">
            <Button 
              className="w-full" 
              onClick={() => navigate({ to: '/' })}
            >
              Back to Home
            </Button>
            
            <Button 
              variant="outline"
              className="w-full" 
              onClick={() => navigate({ to: '/join', search: { code: undefined } })}
            >
              Join Another Quiz
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultsPage;